import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogRef } from '@angular/material/dialog'
import {
  ElderHeaderComponent,
  ElderThemeDirective,
  Interval,
  IsoIntervalPipe,
} from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { WorkCenter } from '../../../../shopfloor/workcenters/work-center'
import { WorkCenterService } from '../../../../shopfloor/workcenters/work-center.service'
import { ControlTerminalDialogService } from '../../../../terminals/dialog/control-terminal-dialog.service'
import {
  IProductionListDialogOptions,
  ProductionListDialogComponent,
} from '../../../../production/lists/production-list-dialog/production-list-dialog.component'
import { ControlTerminalMode } from '../../../../terminals/control-terminal-mode'
import {
  IProductionListConfSelectDialogOptions,
  ProductionListConfSelectDialogComponent,
} from '../../../../production/lists/conf/production-list-conf-select-dialog/production-list-conf-select-dialog.component'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ProductionListScope } from '../../../../production/lists/conf/prod-list-scope'
import { MexusViewMode } from '../../../../context/view-mode-context/mexus-view-mode'
import { Instant } from '@js-joda/core'
import { WorkCenterTypeService } from '../../../../shopfloor/workcenters/types/work-center-type.service'
import { filter, mergeMap } from 'rxjs/operators'
import {
  WorkItemConstraintsDialogComponent,
  WorkItemFilterDialogData,
} from '../../../work-items/work-item-filter-dialog/work-item-constraints-dialog.component'
import { ConstraintInput } from '../../../../shopfloor/workcenters/capability/constraints/constraintInput'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { WorkCenterUtilisationDetailComponent } from '../work-center-utilisation-detail/work-center-utilisation-detail.component'
import { MatBadge } from '@angular/material/badge'
import { MatIcon } from '@angular/material/icon'
import { MatIconButton } from '@angular/material/button'
import { MatToolbar } from '@angular/material/toolbar'
import { AsyncPipe, DatePipe } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

export interface IWCUtilisationDialogOptions {
  readonly workCenterId: string
  readonly scheduleInterval: Interval
  readonly viewMode: MexusViewMode
}

@Component({
  selector: 'app-work-center-utilisation-detail-dialog',
  templateUrl: './work-center-utilisation-detail-dialog.component.html',
  styleUrls: ['./work-center-utilisation-detail-dialog.component.scss'],
  imports: [
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    ElderHeaderComponent,
    MatBadge,
    WorkCenterUtilisationDetailComponent,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
    IsoIntervalPipe,
  ],
})
export class WorkCenterUtilisationDetailDialogComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  readonly workCenter$: Observable<WorkCenter>
  private readonly _itemConstraints$ = new BehaviorSubject<ConstraintInput[]>([])
  public readonly ViewMode = MexusViewMode
  public readonly currentTime = Instant.now().toString()
  dialogRef = inject<MatDialogRef<WorkCenterUtilisationDetailDialogComponent>>(MatDialogRef)
  readonly data = inject<IWCUtilisationDialogOptions>(MAT_DIALOG_DATA)
  private terminalDialogService = inject(ControlTerminalDialogService)
  private matDialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const workCenterTypeService = inject(WorkCenterTypeService)
    const workCenterService = inject(WorkCenterService)
    this.workCenter$ = workCenterService.findById(this.data.workCenterId)
    this.workCenter$
      .pipe(
        takeUntilDestroyed(),
        filter((wc) => !!wc),
        mergeMap((wc) => workCenterTypeService.findById(wc.type))
      )
      .subscribe((wcType) => {
        this.itemConstraints = wcType?.filterSpecs.map((spec) => new ConstraintInput(spec, []))
      })
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get itemConstraints$(): Observable<ConstraintInput[]> {
    return this._itemConstraints$.asObservable()
  }

  public get itemConstraints(): ConstraintInput[] {
    return this._itemConstraints$.getValue()
  }

  public set itemConstraints(constraints: ConstraintInput[]) {
    this._itemConstraints$.next([...constraints])
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public hasItemConstraints(itemConstraints: ConstraintInput[]): boolean {
    return itemConstraints.length > 0
  }

  public numberOfActiveItemConstraints(itemConstraints: ConstraintInput[]): number {
    return itemConstraints.filter(
      (config) => config?.values?.length > 0 && config?.values[0] != 'false'
    ).length
  }

  public onOpenItemConstraints(itemConstraints: ConstraintInput[]): void {
    this.openItemConstraintsDialog(itemConstraints)
      .afterClosed()
      .subscribe((changes: ConstraintInput[] | '' | undefined) => {
        if (changes) {
          this.itemConstraints = changes
        }
      })
  }

  public openTerminal(workCenter: WorkCenter, scheduleInterval: Interval): void {
    this.terminalDialogService.openTerminalForWorkCenter(workCenter, scheduleInterval, true)
  }

  public onProductionListRequested(workCenter: WorkCenter): void {
    this.openProdListConfSelection([])
      .afterClosed()
      .subscribe((confId) => {
        if (confId) {
          this.openProductionList(confId, workCenter)
        } else {
          this.log.info('No conf id selected!')
        }
      })
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private openItemConstraintsDialog(
    constraints: ConstraintInput[]
  ): MatDialogRef<WorkItemConstraintsDialogComponent> {
    return this.matDialog.open(WorkItemConstraintsDialogComponent, {
      minWidth: '50vw',
      height: '40vh',
      data: <WorkItemFilterDialogData>{
        constraints: constraints,
      },
    })
  }

  private openProductionList(prodListConfId: string, workCenter: WorkCenter): void {
    this.matDialog.open(ProductionListDialogComponent, {
      minWidth: '90vw',
      height: '90vh',
      data: <IProductionListDialogOptions>{
        workCenter: workCenter,
        scheduleInterval: this.data.scheduleInterval,
        modes: [ControlTerminalMode.PREVIEW],
        prodListConfId: prodListConfId,
        hideToolbar: true,
        scopes: [],
      },
    })
  }

  private openProdListConfSelection(
    scopes: ProductionListScope[]
  ): MatDialogRef<ProductionListConfSelectDialogComponent> {
    return this.matDialog.open(ProductionListConfSelectDialogComponent, {
      maxWidth: '30vw',
      maxHeight: '30vh',
      width: '30vw',
      height: '30vh',
      data: <IProductionListConfSelectDialogOptions>{
        scopes: scopes,
      },
    })
  }
}
